import React, { FC, ChangeEvent, useContext, useEffect, useState } from "react";
import { Checkbox, Chip, TextField, Theme } from "@material-ui/core";
import { Clear, CheckBox, CheckBoxOutlineBlank } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { Autocomplete as AutocompleteMUI } from "@material-ui/lab";
import { DataContext, FiltersContext } from "../../context";
import { FILTERS } from "../../constants";
import { filter, sort } from "../../functions";
import StyledTooltip from "../Common/StyledTooltip";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingTop: "0px",
  },
  title: {
    fontSize: 15,
    color: "#003368",
    marginTop: 20,
    marginBottom: 4,
  },
  input: {
    fontSize: 14,
  },
  textField: {
    borderRadius: "7%",
    backgroundColor: theme.palette.background.paper,
  },
  option: {
    fontSize: 14,
  },
  resultRow: {
    marginRight: 5,
    padding: 0,
  },
  tag: {
    backgroundColor: theme.palette.background.default,
    color: "#003368",
    borderRadius: 4,
    marginRight: 8,
    marginBottom: 4,
  },
  tagIcon: {
    color: "#003368",
    width: 12,
    height: 12,
  },
  tooltip: {
    maxWidth: 400,
  },
}));

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

type AutocompleteProps = {
  objectKey: string;
  tooltips?: Record<string, string>;
};

const Autocomplete: FC<AutocompleteProps> = ({ objectKey, tooltips = {} }) => {
  const classes = useStyles();

  const [dataContext, setDataContext] = useContext(DataContext);
  const [filtersContext, setFiltersContext] = useContext(FiltersContext);

  const [options, setOptions] = useState<Array<string>>([]);

  const handleFilters = (section: string, newFilters: string | string[]) => {
    setFiltersContext({
      ...filtersContext,
      [section]: newFilters,
    });
  };

  const handleRemove = (section: string, removedFilter: string) => {
    const remainingFilters = filtersContext[section].filter((appliedFilter) => appliedFilter !== removedFilter);
    handleFilters(section, remainingFilters);
  };

  const handleChange = (event: ChangeEvent<{}>, newValue: string[], section: string) => {
    handleFilters(section, newValue);
  };

  useEffect(() => {
    if (dataContext.length > 0) {
      const filtered = filter.uniqueResults(dataContext, objectKey);
      const sorted = sort.caseInsensitive(filtered);
      setOptions(sorted);
    }
  }, [dataContext]);

  return (
    <div className={classes.container}>
      <div className={classes.title}>{FILTERS.MENU_AUTOCOMPLETES_TITLES[objectKey]}</div>
      <AutocompleteMUI
        id={objectKey}
        classes={{
          input: classes.input,
          option: classes.option,
        }}
        multiple
        limitTags={0}
        getLimitTagsText={(more) => `${more} selected`}
        disableCloseOnSelect
        size="small"
        value={filtersContext[objectKey]}
        onChange={(event, newValue) => handleChange(event, newValue, objectKey)}
        options={options}
        renderOption={(option, { selected }) => (
          <>
            <Checkbox icon={icon} checkedIcon={checkedIcon} className={classes.resultRow} checked={selected} />
            {tooltips[option] ? (
              <StyledTooltip title={tooltips[option]} classes={{ tooltip: classes.tooltip }}>
                <span>{option}</span>
              </StyledTooltip>
            ) : (
              option
            )}
          </>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            className={classes.textField}
            inputProps={{
              ...params.inputProps,
              autoComplete: "new-password",
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              className={classes.tag}
              size="small"
              label={option}
              deleteIcon={<Clear className={classes.tagIcon} />}
              onDelete={() => handleRemove(objectKey, option)}
            />
          ))
        }
      />
    </div>
  );
};

export default Autocomplete;
